import type { BackendTask } from './api';
import type { MockTask, TaskStatus, TaskPriority } from './mock-data';

const STATUS_MAP: Record<string, TaskStatus> = {
  // DB tasks_status_check é lowercase: pending|running|retry|done|failed|
  // cancelled|paused. Mantemos uppercase como back-compat defensiva.
  // PAUSED roteia pra coluna 'paused' (não 'bloqueado' como antes).
  PENDING: 'pendente',
  RUNNING: 'em_progresso',
  PAUSED: 'paused',
  DONE: 'concluido',
  FAILED: 'bloqueado',
  RETRY: 'pendente',
  CANCELLED: 'cancelado',
  pending: 'pendente',
  running: 'em_progresso',
  paused: 'paused',
  done: 'concluido',
  failed: 'bloqueado',
  retry: 'pendente',
  cancelled: 'cancelado',
};

const SIZING_TO_PRIORITY: Record<string, TaskPriority> = {
  LONGA: 'P1',
  MEDIA: 'P2',
  CURTA: 'P3',
  MICRO: 'P3',
  longa: 'P1',
  media: 'P2',
  curta: 'P3',
  micro: 'P3',
};

const URGENCY_TO_PRIORITY: Record<string, TaskPriority> = {
  critical: 'P1',
  high: 'P1',
  medium: 'P2',
  low: 'P3',
  P1: 'P1',
  P2: 'P2',
  P3: 'P3',
};

export function mapBackendToKanban(task: BackendTask): MockTask {
  // Kanban perf 2026-05-06 — list endpoint faz select enxuto e omite
  // input/output/error. Antes (legado), title vinha de input.scope/action.
  // Agora preferimos task.title (coluna dedicada), com fallback pra input.scope
  // quando caller traz input (ex: GET /tasks/:id no detail modal).
  const input = task.input ?? {};
  const scope = (input['scope'] as string) ?? '';
  const action = (input['action'] as string) ?? '';
  const isSubTask = !!task.parent_task_id;

  // Fallback hierárquico pro title:
  // 1. tasks.title (coluna dedicada — preferida quando preenchida)
  // 2. input.scope (legado)
  // 3. target_slug (mais útil que event_type "dispatch.X" — vê o que de fato roda)
  // 4. event_type (último recurso)
  let title = (task.title ?? '').trim();
  if (!title) {
    if (isSubTask && action) {
      title = `[${action}] ${scope.slice(0, 80) || task.target_slug || task.event_type}`;
    } else {
      title = scope || (task.target_slug ?? '') || task.event_type || '(sem título)';
    }
  }

  // Priority: urgency_level (coluna nova) ganha de sizing quando presente.
  const priority: TaskPriority =
    (task.urgency_level ? URGENCY_TO_PRIORITY[task.urgency_level] : undefined) ??
    SIZING_TO_PRIORITY[task.sizing] ??
    'P2';

  return {
    id: task.id,
    title,
    status: STATUS_MAP[task.status] ?? 'pendente',
    priority,
    category: task.event_type.split('.')[0] ?? 'engine',
    workflow_type: action || 'pipeline',
    project: 'raios',
    engineer: task.handler,
    created_at: task.created_at,
    description: task.description ?? scope,
    subtasks: undefined,
    output: task.output,
    error: task.error,
    parent_task_id: task.parent_task_id ?? undefined,
    // Card rico — campos novos (todos opcionais p/ back-compat)
    target_slug: task.target_slug ?? null,
    started_at: task.started_at ?? null,
    completed_at: task.completed_at ?? null,
    estimated_duration_seconds: task.estimated_duration_seconds ?? null,
    // G4 F1 (D-G4-F): backend canonical é tasks.duration_ms (G3 PR-P1).
    duration_ms: task.duration_ms ?? null,
    retry_count: task.retry_count,
    max_retries: task.max_retries,
    demand_id: task.demand_id ?? null,
    session_id: task.session_id ?? null,
    companion_id: task.companion_id ?? null,
    agent_id: task.agent_id ?? null,
    updatedAt: task.updated_at,
  };
}

export function mapKanbanStatusToBackend(kanbanStatus: string): string {
  // Reverse: kanban col id → status do DB.
  // 'em_revisao' e 'aprovado' são colunas frontend pra futuro — backend ainda
  // não tem esses statuses (tasks_status_check só tem pending|running|retry|
  // done|failed|cancelled|paused). Retornamos sentinela __no_match__ pra
  // request retornar 0 rows (coluna fica vazia, sem ruído).
  const reverse: Record<string, string> = {
    pendente: 'pending',
    em_progresso: 'running',
    paused: 'paused',
    em_revisao: '__no_match__',
    aprovado: '__no_match__',
    concluido: 'done',
    bloqueado: 'failed',
    cancelado: 'cancelled',
  };
  return reverse[kanbanStatus] ?? 'pending';
}

export function kanbanStatusToBackendUpper(kanbanStatus: TaskStatus): string {
  return mapKanbanStatusToBackend(kanbanStatus).toUpperCase();
}
